<template>
  <div class="bank-info-container">
    <!-- Search Condition -->
    <el-card class="search-wrapper">
      <div slot="header">
        <span>{{ $t('bankInfo.search.title') }}</span>
      </div>
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.bankName" :placeholder="$t('bankInfo.search.bankName')" clearable style="width: 220px" />
          <el-button type="primary" @click="handleSearch">
            <i class="el-icon-search"></i>
            {{ $t('common.search') }}
          </el-button>
          <el-button @click="handleReset">
            <i class="el-icon-refresh"></i>
            {{ $t('common.reset') }}
          </el-button>
        </div>
        <div class="search-right">
          <el-button type="primary" @click="handleAdd">
            <i class="el-icon-plus"></i>
            {{ $t('common.add') }}
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- Data List -->
    <el-card class="list-wrapper">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('bankInfo.list.title') }}</span>
      </div>

      <el-table v-loading="loading" :data="tableData" border style="width: 100%">
        <el-table-column type="index" :label="$t('bankInfo.list.id')" align="center" width="60" />
        <el-table-column prop="bankName" :label="$t('bankInfo.list.bankName')" align="center" min-width="160" />
        <el-table-column prop="bankFee" :label="$t('bankInfo.list.bankFee')" align="center" width="160">
          <template slot-scope="scope">
            <span style="color: #e6a23c; font-weight: bold">{{ Number(scope.row.bankFee || 0).toFixed(2) }}</span>
            元/笔
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('bankInfo.list.remark')" align="center" min-width="180">
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('bankInfo.list.createTime')" align="center" width="160" />
        <el-table-column :label="$t('common.operation')" align="center" width="160">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button type="text" size="small" style="color: #f56c6c" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <div class="tip-left">
          <el-alert :title="$t('bankInfo.tip')" type="info" :closable="false" />
        </div>
        <div class="pager-right">
          <el-pagination
            background
            layout="total, prev, pager, next, jumper"
            :total="total"
            :current-page.sync="currentPage"
            :page-size="pageSize"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="480px" :close-on-click-modal="false">
      <el-form ref="bankForm" :model="bankForm" :rules="bankRules" label-width="110px">
        <el-form-item :label="$t('bankInfo.form.bankName')" prop="bankName">
          <el-input v-model="bankForm.bankName" :placeholder="$t('bankInfo.form.bankNamePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('bankInfo.form.bankFee')" prop="bankFee">
          <el-input-number v-model="bankForm.bankFee" :min="0" :precision="2" :step="0.5" style="width: 100%" />
          <div class="form-tip">{{ $t('bankInfo.form.bankFeeTip') }}</div>
        </el-form-item>
        <el-form-item :label="$t('bankInfo.form.remark')">
          <el-input v-model="bankForm.remark" type="textarea" :rows="2" :placeholder="$t('bankInfo.form.remarkPlaceholder')" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listBankInfo, saveBankInfo, updateBankInfo, deleteBankInfo } from '@/api/system/bankInfoApi'

export default {
  name: 'bankInfoList',
  data() {
    return {
      loading: false,
      saving: false,
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        bankName: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      bankForm: {
        bankId: '',
        bankName: '',
        bankFee: 0,
        remark: ''
      },
      bankRules: {
        bankName: [{ required: true, message: this.$t('bankInfo.form.bankNameRequired'), trigger: 'blur' }],
        bankFee: [{ required: true, message: this.$t('bankInfo.form.bankFeeRequired'), trigger: 'change' }]
      }
    }
  },
  created() {
    this._loadData()
  },
  methods: {
    _loadData() {
      this.loading = true
      const params = {
        page: this.currentPage,
        row: this.pageSize,
        bankName: this.searchForm.bankName || ''
      }
      listBankInfo(params).then(res => {
        this.loading = false
        if (res.code === 0) {
          this.tableData = res.data || []
          this.total = res.total || 0
        } else {
          this.$message.error(res.msg || '查询失败')
        }
      }).catch(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.currentPage = 1
      this._loadData()
    },
    handleReset() {
      this.searchForm = { bankName: '' }
      this.currentPage = 1
      this._loadData()
    },
    handlePageChange() {
      this._loadData()
    },
    handleAdd() {
      this.bankForm = { bankId: '', bankName: '', bankFee: 0, remark: '' }
      this.dialogTitle = this.$t('bankInfo.dialog.add')
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.bankForm = {
        bankId: row.bankId,
        bankName: row.bankName,
        bankFee: Number(row.bankFee || 0),
        remark: row.remark || ''
      }
      this.dialogTitle = this.$t('bankInfo.dialog.edit')
      this.dialogVisible = true
    },
    handleSave() {
      this.$refs.bankForm.validate(valid => {
        if (!valid) {
          return false
        }
        this.saving = true
        const data = {
          bankName: this.bankForm.bankName,
          bankFee: this.bankForm.bankFee,
          remark: this.bankForm.remark || ''
        }
        const api = this.bankForm.bankId ? updateBankInfo : saveBankInfo
        if (this.bankForm.bankId) {
          data.bankId = this.bankForm.bankId
        }
        api(data).then(res => {
          this.saving = false
          if (res.code === 0) {
            this.$message.success(this.$t('bankInfo.saveSuccess'))
            this.dialogVisible = false
            this._loadData()
          } else {
            this.$message.error(res.msg || '保存失败')
          }
        }).catch(() => {
          this.saving = false
        })
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('bankInfo.deleteConfirm'), this.$t('common.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        deleteBankInfo({ bankId: row.bankId }).then(res => {
          if (res.code === 0) {
            this.$message.success(this.$t('bankInfo.deleteSuccess'))
            this._loadData()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* 搜索栏：选项靠左、添加按钮靠右 */
.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.search-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}
.search-left .el-input {
  margin-right: 10px;
}
.search-left .el-button {
  margin-right: 10px;
}
.search-right {
  flex-shrink: 0;
  margin-left: 12px;
}
/* 分页行：小字靠左、分页靠右 */
.pagination-wrapper {
  margin-top: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.pagination-wrapper .tip-left {
  flex: 1;
  margin-right: 12px;
  min-width: 0;
}
.pagination-wrapper .tip-left /deep/ .el-alert {
  padding: 6px 12px;
  line-height: 1.5;
}
.pagination-wrapper .pager-right {
  flex-shrink: 0;
}
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin-top: 4px;
}
</style>
